import React, { useState, useEffect } from 'react';
import NoticeCard from '../Common/NoticeCard';
import { Notice } from '../../types/Notice';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const NoticesPage: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'central' | 'building' | 'history'>('all');
  const [priority, setPriority] = useState<'all' | 'urgent' | 'high' | 'medium' | 'low'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { t } = useTranslation();

  useEffect(() => {
    fetchNotices();
  }, [filter, priority, searchTerm, currentPage]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filter !== 'all' && filter !== 'history') {
        params.append('type', filter);
      }
      if (priority !== 'all') {
        params.append('priority', priority);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('page', currentPage.toString());
      params.append('per_page', '12');

      const endpoint = filter === 'history' ? '/notices/history' : '/notices';
      const response = await api.get(`${endpoint}?${params}`);
      
      if (response.data.success) {
        setNotices(response.data.data.data || response.data.data);
        setTotalPages(response.data.data.last_page || 1);
      } else {
        setError(response.data.message || t('public.notices.fetchError'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('public.notices.fetchError'));
      console.error('Error fetching notices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter: 'all' | 'central' | 'building' | 'history') => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handlePriorityChange = (newPriority: 'all' | 'urgent' | 'high' | 'medium' | 'low') => {
    setPriority(newPriority);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchNotices();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setFilter('all');
    setPriority('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getPriorityCount = (priorityType: string) => {
    return notices.filter(notice => notice.priority === priorityType).length;
  };

  const getUrgentNotices = () => {
    return notices.filter(notice => notice.is_urgent || notice.priority === 'urgent');
  };

  if (loading) {
    return (
      <div className="notices-page-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>{t('noticesPage.loadingTitle')}</h3>
          <p>{t('noticesPage.loadingSubtitle')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notices-page-error">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>{t('noticesPage.errorTitle')}</h3>
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={fetchNotices}
          >
            {t('noticesPage.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  const urgentNotices = getUrgentNotices();
  const regularNotices = notices.filter(notice => !notice.is_urgent && notice.priority !== 'urgent');

  return (
    <div className="notices-page">
      {/* Hero Section */}
      <div className="notices-hero">
        <div className="hero-content">
          <h1>{t('noticesPage.heroTitle')}</h1>
          <p>{t('noticesPage.heroSubtitle')}</p>
        </div>
        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-number">{notices.length}</span>
            <span className="stat-label">{t('noticesPage.stats.total')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{urgentNotices.length}</span>
            <span className="stat-label">{t('noticesPage.stats.urgent')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{getPriorityCount('high')}</span>
            <span className="stat-label">{t('noticesPage.stats.high')}</span>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="notices-filters-section">
        <div className="filters-container">
          {/* Search */}
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                placeholder={t('noticesPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">
                🔍
              </button>
            </div>
          </form>

          {/* Type Filters */}
          <div className="filter-group">
            <label>{t('noticesPage.typeLabel')}</label>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => handleFilterChange('all')}
              >
                {t('noticesPage.typeAll')}
              </button>
              <button
                className={`filter-btn ${filter === 'central' ? 'active' : ''}`}
                onClick={() => handleFilterChange('central')}
              >
                {t('noticesPage.typeCentral')}
              </button>
              <button
                className={`filter-btn ${filter === 'building' ? 'active' : ''}`}
                onClick={() => handleFilterChange('building')}
              >
                {t('noticesPage.typeBuilding')}
              </button>
              {/* <button
                className={`filter-btn ${filter === 'history' ? 'active' : ''}`}
                onClick={() => handleFilterChange('history')}
              >
                Historical
              </button> */}
            </div>
          </div>

          {/* Priority Filters */}
          <div className="filter-group">
            <label>{t('noticesPage.priorityLabel')}</label>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${priority === 'all' ? 'active' : ''}`}
                onClick={() => handlePriorityChange('all')}
              >
                {t('noticesPage.priorityAll')}
              </button>
              <button
                className={`filter-btn urgent ${priority === 'urgent' ? 'active' : ''}`}
                onClick={() => handlePriorityChange('urgent')}
              >
                {t('noticesPage.priorityUrgent')}
              </button>
              <button
                className={`filter-btn high ${priority === 'high' ? 'active' : ''}`}
                onClick={() => handlePriorityChange('high')}
              >
                {t('noticesPage.priorityHigh')}
              </button>
              <button
                className={`filter-btn medium ${priority === 'medium' ? 'active' : ''}`}
                onClick={() => handlePriorityChange('medium')}
              >
                {t('noticesPage.priorityMedium')}
              </button>
              <button
                className={`filter-btn low ${priority === 'low' ? 'active' : ''}`}
                onClick={() => handlePriorityChange('low')}
              >
                {t('noticesPage.priorityLow')}
              </button>
            </div>
          </div>

          {/* View Mode */}
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title={t('noticesPage.viewGrid')}
            >
              ⊞
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title={t('noticesPage.viewList')}
            >
              ☰
            </button>
          </div>

          {/* Clear Filters */}
          <button
            className="clear-filters-btn"
            onClick={clearFilters}
          >
            {t('noticesPage.clearAll')}
          </button>
        </div>
      </div>

      {/* Urgent Notices Section */}
      {urgentNotices.length > 0 && (
        <div className="urgent-notices-section">
          <div className="section-header">
            <h2>{t('noticesPage.urgentSection')}</h2>
            <div className="urgent-badge">{urgentNotices.length}</div>
          </div>
          <div className={`notices-container ${viewMode} urgent`}>
            {urgentNotices.map((notice) => (
              <NoticeCard
                key={notice.id}
                notice={notice}
                showFullContent={false}
                className="urgent-notice-card"
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular Notices Section */}
      <div className="regular-notices-section">
        <div className="section-header">
          <h2>{t('noticesPage.allNotices')}</h2>
          <div className="results-count">
            {t('noticesPage.resultsCount', { count: notices.length })}
          </div>
        </div>

        {notices.length === 0 ? (
          <div className="no-notices">
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <h3>{t('noticesPage.noResultsTitle')}</h3>
              <p>{t('noticesPage.noResultsDescription')}</p>
              <button
                className="btn btn-primary"
                onClick={clearFilters}
              >
                {t('noticesPage.clearFilters')}
              </button>
            </div>
          </div>
        ) : (
          <div className={`notices-container ${viewMode}`}>
            {regularNotices.map((notice) => (
              <NoticeCard
                key={notice.id}
                notice={notice}
                showFullContent={false}
                className={viewMode === 'list' ? 'list-item' : ''}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-section">
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t('noticesPage.pagination.previous')}
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t('noticesPage.pagination.next')}
              </button>
            </div>
            
            <div className="pagination-info">
              {t('noticesPage.pagination.pageInfo', {
                current: currentPage,
                total: totalPages,
                count: notices.length
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticesPage;
